import { useDataTestid as useDataTestidCore } from '@cplug/frontend-core/composables'

/**
 * Composable para gerar data-testid padronizados
 * Padrão: dashboard-${subdomain}-${component}-${element}-${itemKey?}
 */

// Constantes para domínios e subdomínios mais organizados
export const DATA_TESTID_PREFIX = 'dashboard' as const

export const DATA_TESTID_SUBDOMAINS = {
  // Auth & User
  LOGIN: 'login',
  SIGNUP: 'signup',
  PROFILE: 'profile',
  USER: 'user',

  // Navigation & Layout
  MENU: 'menu',
  PAGE: 'page',
  NAV: 'nav',
  SIDEBAR: 'sidebar',

  // Data & Content
  INDEX_PAGE: 'index-page',
  INDEX_FILTER: 'index-filter',
  FULL_TABLE: 'full-table',
  FORM: 'form',

  // Business Domains
  CATALOG_PRODUCT: 'catalog-product',
  CATALOG_CATEGORY: 'catalog-category',
  STOCK: 'stock',
  FINANCE: 'finance',
  CUSTOMER: 'customer',
  SALES: 'sales',
  CATALOG_PRICE_LIST: 'catalog-price-list',
  CATALOG_TECHNICAL_SHEET: 'catalog-technical-sheet',

  // Operations
  ORDER: 'order',
  PAYMENT: 'payment',
  SHIPPING: 'shipping',
  INVOICE: 'invoice',

  // System
  SETTINGS: 'settings',
  REPORT: 'report',
  NOTIFICATION: 'notification',

  // Entities
  BRAND: 'brand',
  SUPPLIER: 'supplier',
  COMPANY: 'company',
  TAX: 'tax',
  ROLE: 'role',
  PERMISSION: 'permission',
  GROUP: 'group'
} as const

export const DATA_TESTID_ELEMENTS = {
  // Form Elements
  INPUT: 'input',
  BUTTON: 'button',
  SELECT: 'select',
  TEXTAREA: 'textarea',
  CHECKBOX: 'checkbox',
  RADIO: 'radio',
  SWITCH: 'switch',
  TOGGLE: 'toggle',

  // Specialized Inputs
  SLIDER: 'slider',
  RANGE: 'range',
  DATE: 'date',
  TIME: 'time',
  DATETIME: 'datetime',
  COLOR: 'color',
  FILE: 'file',

  // Media
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',

  // Navigation
  LINK: 'link',
  NAV: 'nav',

  // Content
  TEXT: 'text',
  TITLE: 'title',
  SUBTITLE: 'subtitle',
  DESCRIPTION: 'description',
  ICON: 'icon',

  // Layout
  CONTAINER: 'container',
  MODAL: 'modal',
  TABLE: 'table',
  GRID: 'grid',
  COL: 'col',
  ROW: 'row',
  HEADER: 'header',
  FOOTER: 'footer',
  FAB: 'fab'
} as const

// Tipos para melhor TypeScript experience
export type DataTestidSubdomain = typeof DATA_TESTID_SUBDOMAINS[keyof typeof DATA_TESTID_SUBDOMAINS]
export type DataTestidElement = typeof DATA_TESTID_ELEMENTS[keyof typeof DATA_TESTID_ELEMENTS]

/**
 * Função principal para gerar data-testid
 * @param subdomain - Subdomínio/área funcional
 * @param component - Nome do componente
 * @param element - Tipo do elemento HTML
 * @param itemKey - Chave opcional para identificar item específico
 * @returns string no formato: dashboard-${subdomain}-${component}-${element}-${itemKey?}
 */
export const useDataTestid = (
  subdomain: DataTestidSubdomain | string,
  component: string,
  element: DataTestidElement | string,
  itemKey?: string | number
): string => {
  return useDataTestidCore(DATA_TESTID_PREFIX, subdomain, component, element, itemKey)
}
